'use client';

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HexColorPicker } from 'react-colorful';
import { Plus, X, Trash2 } from 'lucide-react';

/**
 * Photoshop-style Gradient Editor
 * 
 * Features:
 * - Visual gradient bar with draggable color stop tabs
 * - Click on bar to add new color stops
 * - Click tab to select and change color
 * - Drag tabs to change position
 * - Delete tab with X button
 * - Linear/Radial toggle
 * - Rotation control for linear gradients
 */
export default function PhotoshopGradientEditor({ value, onChange, label = 'Gradient' }) {
  const gradientBarRef = useRef(null);
  const selectedStopPreviewRef = useRef(null);
  const solidPreviewRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedStopIndex, setDraggedStopIndex] = useState(null);
  const [activePointerId, setActivePointerId] = useState(null);
  
  // Parse gradient or initialize
  const parseGradient = (gradientString) => {
    if (!gradientString) {
      return {
        isGradient: false,
        type: 'linear',
        rotation: 135,
        stops: [{ color: '#1a1a2e', position: 0 }]
      };
    }
    
    const isLinear = gradientString.startsWith('linear-gradient');
    const isRadial = gradientString.startsWith('radial-gradient');
    
    if (!isLinear && !isRadial) {
      // Solid color
      return {
        isGradient: false,
        type: 'linear',
        rotation: 135,
        stops: [{ color: gradientString, position: 0 }]
      };
    }
    
    // Extract rotation for linear
    let rotation = 135;
    if (isLinear) {
      const rotationMatch = gradientString.match(/linear-gradient\((\d+)deg/);
      if (rotationMatch) {
        rotation = parseInt(rotationMatch[1]);
      }
    }
    
    // Extract color stops
    const stopsMatch = gradientString.match(/(#[0-9a-fA-F]{6}|rgb\([^)]+\)|rgba\([^)]+\))\s+(\d+)%/g);
    const stops = [];
    
    if (stopsMatch) {
      stopsMatch.forEach(stopStr => {
        const match = stopStr.match(/(#[0-9a-fA-F]{6}|rgb\([^)]+\)|rgba\([^)]+\))\s+(\d+)%/);
        if (match) {
          stops.push({
            color: match[1],
            position: parseInt(match[2])
          });
        }
      });
    }
    
    if (stops.length === 0) {
      const colorsOnly = gradientString.match(/(#[0-9a-fA-F]{6}|rgb\([^)]+\)|rgba\([^)]+\))/g) || [];
      if (colorsOnly.length >= 2) {
        const step = 100 / (colorsOnly.length - 1);
        colorsOnly.forEach((color, index) => {
          stops.push({
            color,
            position: Math.round(step * index)
          });
        });
      } else {
        stops.push({ color: '#1a1a2e', position: 0 });
        stops.push({ color: '#4a5568', position: 100 });
      }
    }
    
    return {
      isGradient: true,
      type: isRadial ? 'radial' : 'linear',
      rotation,
      stops
    };
  };
  
  const [gradient, setGradient] = useState(parseGradient(value));
  const [selectedStopIndex, setSelectedStopIndex] = useState(0);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const lastValueRef = useRef(value);
  const [portalTarget, setPortalTarget] = useState(null);
  const normalizeStops = (stops = []) => (
    [...stops]
      .map((stop) => ({
        color: stop.color,
        position: Math.max(0, Math.min(100, Math.round(stop.position)))
      }))
      .sort((a, b) => a.position - b.position)
  );
  const normalizeGradientValue = (gradientString) => {
    const parsed = parseGradient(gradientString);
    return JSON.stringify({
      isGradient: parsed.isGradient,
      type: parsed.type,
      rotation: parsed.rotation,
      stops: normalizeStops(parsed.stops)
    });
  };
  const normalizeGradientState = (state) => JSON.stringify({
    isGradient: state.isGradient,
    type: state.type,
    rotation: state.rotation,
    stops: normalizeStops(state.stops)
  });
  
  // Generate CSS gradient string
  const generateGradient = () => {
    if (!gradient.isGradient) {
      return gradient.stops[0].color;
    }
    
    const sortedStops = [...gradient.stops].sort((a, b) => a.position - b.position);
    const stopsStr = sortedStops.map(s => `${s.color} ${s.position}%`).join(', ');
    
    if (gradient.type === 'radial') {
      return `radial-gradient(circle, ${stopsStr})`;
    } else {
      return `linear-gradient(${gradient.rotation}deg, ${stopsStr})`;
    }
  };
  
  // Update parent when gradient changes
  useEffect(() => {
    const newValue = generateGradient();
    if (newValue !== value) {
      lastValueRef.current = newValue;
      onChange(newValue);
    }
  }, [gradient, value, onChange]);

  // Use a ref to track if we're currently making changes
  const isInteractingRef = useRef(false);
  const interactionTimeoutRef = useRef(null);
  
  useEffect(() => {
    if (value === lastValueRef.current) return;
    // Don't sync external changes while user is actively interacting
    if (isDragging || showPicker || isInteractingRef.current) return;
    if (normalizeGradientValue(value) === normalizeGradientState(gradient)) {
      lastValueRef.current = value;
      return;
    }
    lastValueRef.current = value;
    setGradient(parseGradient(value));
    setSelectedStopIndex(0);
    setShowPicker(false);
  }, [value, isDragging, showPicker]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setPortalTarget(document.body);
    
    // Cleanup timeout on unmount
    return () => {
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, []);
  
  // Handle clicking on gradient bar to add stop
  const handleBarClick = (e) => {
    if (e.target !== gradientBarRef.current) return;
    
    const rect = gradientBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const position = Math.round((x / rect.width) * 100);
    
    // Get color at this position by interpolating
    const sortedStops = [...gradient.stops].sort((a, b) => a.position - b.position);
    let color = sortedStops[0].color;
    
    for (let i = 0; i < sortedStops.length - 1; i++) {
      if (position >= sortedStops[i].position && position <= sortedStops[i + 1].position) {
        // Use the closer stop's color
        const dist1 = Math.abs(position - sortedStops[i].position);
        const dist2 = Math.abs(position - sortedStops[i + 1].position);
        color = dist1 < dist2 ? sortedStops[i].color : sortedStops[i + 1].color;
        break;
      }
    }
    
    const newStops = [...gradient.stops, { color, position }];
    setGradient({ ...gradient, stops: newStops });
    setSelectedStopIndex(newStops.length - 1);
  };
  
  // Handle dragging color stop
  const handleStopPointerDown = (index, e) => {
    e.stopPropagation();
    if (e.currentTarget?.setPointerCapture && e.pointerId !== undefined) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    setIsDragging(true);
    setDraggedStopIndex(index);
    setSelectedStopIndex(index);
    setActivePointerId(e.pointerId ?? null);
  };
  
  const handlePointerMove = (e) => {
    if (!isDragging || draggedStopIndex === null || !gradientBarRef.current) return;
    if (activePointerId !== null && e.pointerId !== activePointerId) return;
    
    const rect = gradientBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const position = Math.max(0, Math.min(100, Math.round((x / rect.width) * 100)));
    
    const newStops = [...gradient.stops];
    newStops[draggedStopIndex] = { ...newStops[draggedStopIndex], position };
    setGradient({ ...gradient, stops: newStops });
  };
  
  const handlePointerUp = (e) => {
    if (activePointerId !== null && e.pointerId !== activePointerId) return;
    setIsDragging(false);
    setDraggedStopIndex(null);
    setActivePointerId(null);
  };
  
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerUp);
      return () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('pointercancel', handlePointerUp);
      };
    }
  }, [isDragging, draggedStopIndex, activePointerId, gradient]);
  
  // Handle color change for selected stop
  const handleColorChange = (color) => {
    isInteractingRef.current = true;
    const newStops = [...gradient.stops];
    newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], color: color.toLowerCase() };
    setGradient({ ...gradient, stops: newStops });
    // Clear interaction flag after a brief delay
    setTimeout(() => { isInteractingRef.current = false; }, 100);
  };

  const openPickerAt = (target) => {
    const element = target?.current ?? target;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    const pickerWidth = 264;
    const pickerHeight = 280;
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    
    // Calculate position relative to viewport, with preference for below
    let nextTop = rect.bottom + 8;
    let nextLeft = Math.max(
      12,
      Math.min(rect.left, viewportWidth - pickerWidth - 12)
    );
    
    // If picker would go off bottom of screen, try to position above instead
    if (nextTop + pickerHeight > viewportHeight - 12) {
      const topPosition = rect.top - pickerHeight - 8;
      if (topPosition > 12) {
        nextTop = topPosition;
      } else {
        // If neither works, center vertically on screen
        nextTop = Math.max(12, (viewportHeight - pickerHeight) / 2);
      }
    }
    
    setPickerPosition({ top: nextTop, left: nextLeft });
    setShowPicker(true);
  };
  
  // Delete color stop
  const handleDeleteStop = (index) => {
    if (gradient.stops.length <= 2) return; // Must have at least 2 stops
    
    const newStops = gradient.stops.filter((_, i) => i !== index);
    setGradient({ ...gradient, stops: newStops });
    
    if (selectedStopIndex >= newStops.length) {
      setSelectedStopIndex(newStops.length - 1);
    }
  };
  
  return (
    <div className="photoshop-gradient-editor">
      <div className="gradient-header">
        <div className="gradient-title">
          <label className="gradient-label">{label}</label>
          <div className="gradient-swatch" style={{ background: generateGradient() }} />
        </div>
        <label className="gradient-checkbox">
          <input
            type="checkbox"
            checked={gradient.isGradient}
            onChange={(e) => {
              isInteractingRef.current = true;
              setGradient({ ...gradient, isGradient: e.target.checked });
              if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
              interactionTimeoutRef.current = setTimeout(() => {
                isInteractingRef.current = false;
              }, 300);
            }}
          />
          <span>Enable Gradient</span>
        </label>
      </div>
      
      {gradient.isGradient && (
        <>
          {/* Gradient Type Toggle */}
          <div className="gradient-type-selector">
            <button
              className={`type-btn ${gradient.type === 'linear' ? 'type-btn--active' : ''}`}
              onClick={() => {
                isInteractingRef.current = true;
                setGradient({ ...gradient, type: 'linear' });
                if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
                interactionTimeoutRef.current = setTimeout(() => {
                  isInteractingRef.current = false;
                }, 300);
              }}
            >
              Linear
            </button>
            <button
              className={`type-btn ${gradient.type === 'radial' ? 'type-btn--active' : ''}`}
              onClick={() => {
                isInteractingRef.current = true;
                setGradient({ ...gradient, type: 'radial' });
                if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
                interactionTimeoutRef.current = setTimeout(() => {
                  isInteractingRef.current = false;
                }, 300);
              }}
            >
              Radial
            </button>
          </div>
          
          {/* Rotation (linear only) */}
          {gradient.type === 'linear' && (
            <div className="gradient-control">
              <label className="gradient-control-label">Rotation: {gradient.rotation}°</label>
              <input
                type="range"
                min="0"
                max="360"
                value={gradient.rotation}
                onChange={(e) => {
                  isInteractingRef.current = true;
                  setGradient({ ...gradient, rotation: parseInt(e.target.value) });
                  if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
                  interactionTimeoutRef.current = setTimeout(() => {
                    isInteractingRef.current = false;
                  }, 300);
                }}
                className="gradient-slider"
              />
            </div>
          )}
          
          {/* Photoshop-style Gradient Bar */}
          <div className="gradient-bar-container">
            <div className="gradient-bar-label">Click to add, drag to move, click tab to edit</div>
            <div
              ref={gradientBarRef}
              className="gradient-bar"
              onClick={handleBarClick}
              style={{ background: generateGradient() }}
            >
              {gradient.stops.map((stop, index) => (
                <div
                  key={index}
                  className={`gradient-stop ${selectedStopIndex === index ? 'gradient-stop--selected' : ''}`}
                  style={{ 
                    left: `${stop.position}%`,
                    backgroundColor: stop.color
                  }}
                  onPointerDown={(e) => handleStopPointerDown(index, e)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedStopIndex(index);
                    openPickerAt(e.currentTarget);
                  }}
                >
                  {gradient.stops.length > 2 && (
                    <button
                      className="gradient-stop-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteStop(index);
                      }}
                      title="Delete color stop"
                    >
                      <X size={10} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="gradient-bar-ticks">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>
          
          {/* Selected Stop Info */}
          <div className="selected-stop-info">
            <div className="selected-stop-header">
              <span>Selected Color Stop</span>
              <span className="selected-stop-position">{gradient.stops[selectedStopIndex]?.position}%</span>
            </div>
          <div className="selected-stop-controls">
            <div 
              className="selected-stop-preview"
              style={{ backgroundColor: gradient.stops[selectedStopIndex]?.color }}
              onClick={() => (showPicker ? setShowPicker(false) : openPickerAt(selectedStopPreviewRef))}
              ref={selectedStopPreviewRef}
            />
            <input
              type="text"
              value={gradient.stops[selectedStopIndex]?.color || ''}
              onChange={(e) => handleColorChange(e.target.value)}
              placeholder="#RRGGBB"
              className="selected-stop-input"
            />
            {gradient.stops.length > 2 && (
              <button
                type="button"
                className="selected-stop-remove"
                onClick={() => handleDeleteStop(selectedStopIndex)}
              >
                <Trash2 size={14} />
                Remove
              </button>
            )}
          </div>
            
            {showPicker && portalTarget && createPortal(
              <>
                <div 
                  className="picker-backdrop"
                  onClick={() => setShowPicker(false)}
                />
                <div
                  className="picker-popover"
                  style={{
                    top: pickerPosition?.top ?? 0,
                    left: pickerPosition?.left ?? 0
                  }}
                >
                  <HexColorPicker 
                    color={gradient.stops[selectedStopIndex]?.color || '#000000'} 
                    onChange={handleColorChange} 
                  />
                </div>
              </>,
              portalTarget
            )}
          </div>
        </>
      )}
      
      {!gradient.isGradient && (
        <div className="solid-color-section">
          <div className="solid-color-controls">
            <div 
              className="solid-color-preview"
              style={{ backgroundColor: gradient.stops[0].color }}
              onClick={() => (showPicker ? setShowPicker(false) : openPickerAt(solidPreviewRef))}
              ref={solidPreviewRef}
            />
            <input
              type="text"
              value={gradient.stops[0].color}
              onChange={(e) => {
                const newStops = [...gradient.stops];
                newStops[0] = { ...newStops[0], color: e.target.value };
                setGradient({ ...gradient, stops: newStops });
              }}
              placeholder="#RRGGBB"
              className="solid-color-input"
            />
          </div>
          
          {showPicker && portalTarget && createPortal(
            <>
              <div 
                className="picker-backdrop"
                onClick={() => setShowPicker(false)}
              />
              <div
                className="picker-popover"
                style={{
                  top: pickerPosition?.top ?? 0,
                  left: pickerPosition?.left ?? 0
                }}
              >
                <HexColorPicker 
                  color={gradient.stops[0].color} 
                  onChange={(color) => {
                    const newStops = [...gradient.stops];
                    newStops[0] = { ...newStops[0], color: color.toLowerCase() };
                    setGradient({ ...gradient, stops: newStops });
                  }} 
                />
              </div>
            </>,
            portalTarget
          )}
        </div>
      )}
      
      <style jsx>{`
        .photoshop-gradient-editor {
          position: relative;
          padding: 12px;
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.5);
          border-radius: 5px;
          margin-bottom: 16px;
        }
        
        .gradient-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .gradient-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .gradient-label {
          font-size: 13px;
          font-weight: 500;
          color: #cbd5e1;
        }

        .gradient-swatch {
          width: 56px;
          height: 18px;
          border-radius: 5px;
          border: 1px solid rgba(71, 85, 105, 0.5);
        }
        
        .gradient-checkbox {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #cbd5e1;
          cursor: pointer;
        }
        
        .gradient-checkbox input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }
        
        .gradient-type-selector {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }
        
        .type-btn {
          flex: 1;
          padding: 8px 12px;
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.5);
          border-radius: 5px;
          color: #cbd5e1;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .type-btn:hover {
          background: rgba(51, 65, 85, 0.7);
        }
        
        .type-btn--active {
          background: rgba(59, 130, 246, 0.2);
          border-color: #3b82f6;
          color: #60a5fa;
        }
        
        .gradient-control {
          margin-bottom: 12px;
        }
        
        .gradient-control-label {
          display: block;
          font-size: 12px;
          color: #94a3b8;
          margin-bottom: 8px;
        }
        
        .gradient-slider {
          width: 100%;
          height: 6px;
          background: rgba(71, 85, 105, 0.5);
          border-radius: 5px;
          outline: none;
          cursor: pointer;
        }
        
        .gradient-bar-container {
          margin-bottom: 12px;
        }
        
        .gradient-bar-label {
          font-size: 10px;
          color: #64748b;
          margin-bottom: 6px;
          font-style: italic;
        }
        
        .gradient-bar {
          position: relative;
          width: 100%;
          height: 26px;
          border-radius: 5px;
          border: 1px solid rgba(71, 85, 105, 0.6);
          cursor: crosshair;
          margin-bottom: 6px;
          touch-action: none;
        }

        .gradient-stop {
          position: absolute;
          top: -7px;
          width: 16px;
          height: 40px;
          border: 2px solid #475569;
          border-radius: 5px;
          cursor: grab;
          transform: translateX(-50%);
          transition: border-color 0.2s;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding-top: 2px;
          touch-action: none;
        }
        
        .gradient-stop:active {
          cursor: grabbing;
        }
        
        .gradient-stop--selected {
          border-color: #3b82f6;
          border-width: 3px;
          box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
        }
        
        .gradient-stop-delete {
          background: rgba(239, 68, 68, 0.9);
          border: none;
          border-radius: 5px;
          color: white;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s;
        }
        
        .gradient-stop:hover .gradient-stop-delete {
          opacity: 1;
        }
        
        .gradient-bar-ticks {
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          color: #64748b;
          margin-top: 4px;
        }
        
        .selected-stop-info {
          position: relative;
          padding: 10px;
          background: rgba(30, 41, 59, 0.5);
          border-radius: 5px;
        }
        
        .selected-stop-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          font-size: 12px;
          color: #cbd5e1;
        }
        
        .selected-stop-position {
          font-family: monospace;
          color: #60a5fa;
        }
        
        .selected-stop-controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        
        .selected-stop-preview {
          width: 32px;
          height: 32px;
          border: 1px solid rgba(71, 85, 105, 0.6);
          border-radius: 5px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .selected-stop-preview:hover {
          border-color: #3b82f6;
        }
        
        .selected-stop-input {
          flex: 1;
          padding: 8px 10px;
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.5);
          border-radius: 5px;
          color: #e2e8f0;
          font-size: 13px;
          font-family: monospace;
          outline: none;
        }
        
        .selected-stop-input:focus {
          border-color: #3b82f6;
        }

        .selected-stop-remove {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 10px;
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.45);
          border-radius: 5px;
          color: #fecaca;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .selected-stop-remove:hover {
          background: rgba(239, 68, 68, 0.25);
          border-color: rgba(239, 68, 68, 0.65);
          color: #fff1f2;
        }
        
        .solid-color-section {
          position: relative;
        }
        
        .solid-color-controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        
        .solid-color-preview {
          width: 32px;
          height: 32px;
          border: 1px solid rgba(71, 85, 105, 0.6);
          border-radius: 5px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .solid-color-preview:hover {
          border-color: #3b82f6;
        }
        
        .solid-color-input {
          flex: 1;
          padding: 8px 10px;
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.5);
          border-radius: 5px;
          color: #e2e8f0;
          font-size: 13px;
          font-family: monospace;
          outline: none;
        }
        
        .solid-color-input:focus {
          border-color: #3b82f6;
        }
        
        .picker-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 999;
        }
        
        .picker-popover {
          position: fixed;
          z-index: 3000;
          padding: 12px;
          background: var(--glassy-card-bg);
          backdrop-filter: blur(var(--glassy-card-blur));
          -webkit-backdrop-filter: blur(var(--glassy-card-blur));
          border: 1px solid rgba(71, 85, 105, 0.5);
          border-radius: 5px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
        }
        
        .picker-popover :global(.react-colorful) {
          width: 240px;
          height: 240px;
        }
        
        .picker-popover :global(.react-colorful__saturation) {
          border-radius: 5px 5px 0 0;
        }
        
        .picker-popover :global(.react-colorful__hue) {
          height: 20px;
          border-radius: 0 0 5px 5px;
        }
        
        @media (max-width: 768px) {
          .photoshop-gradient-editor {
            padding: 10px;
            overflow-x: hidden;
          }
                    
          .gradient-bar {
            height: 22px;
          }
          
          .gradient-stop {
            width: 14px;
            height: 36px;
            top: -7px;
          }
          
          .gradient-type-selector {
            flex-wrap: wrap;
          }
          
          .picker-popover :global(.react-colorful) {
            width: 200px;
            height: 200px;
          }
          
          .selected-stop-controls {
            flex-direction: column;
            gap: 12px;
          }
          
          .selected-stop-preview {
            width: 100%;
            height: 32px;
          }

          .selected-stop-remove {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}
